// Admin-side support tickets + support chat.
//
// A ticket is a `user_feedback` row (the table the Support Tickets page has
// always read). Its chat thread lives in `support_conversations` /
// `support_messages` — separate tables from brand↔creator messaging, because
// support is customer↔platform with a ticket lifecycle and agent-only internal
// notes. See migration 20260909000000_support_ticket_chat.sql.
//
// Every write goes through a SECURITY DEFINER RPC so the denormalized thread
// fields, the status transitions and the event timeline stay in agreement. This
// module wraps those RPCs and pairs each with an audit-log entry, on both the
// success and the failure path.
import { supabase } from '@/lib/supabase';
import { logCreate, logUpdate } from '@/lib/audit-log';

export type TicketStatus = 'open' | 'in_progress' | 'waiting_on_customer' | 'follow_up_needed' | 'resolved' | 'closed';
export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';
export type TicketType = 'bug_report' | 'feature_request' | 'support' | 'general';
export type SupportSenderType = 'customer' | 'agent' | 'system';
/** Free-form triage labels, distinct from `type` (what the ticket is about) —
 *  a tag answers WHO should work it. `tags` is a plain text[] with no DB
 *  CHECK constraint (see migration 20260922000004_support_ticket_tags.sql),
 *  so the predefined set lives here and can grow without a new migration. */
export type TicketTag = 'technical';

/** Forward progression, and the order the filter chips render in.
 *  waiting_on_customer / follow_up_needed are set automatically (agent reply,
 *  and a 3-day-stale escalation respectively — see migration
 *  20260915000000_support_waiting_on_customer_status.sql) but remain
 *  manually selectable here too. */
export const TICKET_STATUSES: TicketStatus[] = ['open', 'in_progress', 'waiting_on_customer', 'follow_up_needed', 'resolved', 'closed'];
export const TICKET_PRIORITIES: TicketPriority[] = ['low', 'normal', 'high', 'urgent'];
export const TICKET_TYPES: TicketType[] = ['bug_report', 'feature_request', 'support', 'general'];
export const TICKET_TAGS: TicketTag[] = ['technical'];

export const STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting_on_customer: 'Waiting on Customer',
  follow_up_needed: 'Follow-up Needed',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const TYPE_LABELS: Record<TicketType, string> = {
  bug_report: 'Bug',
  feature_request: 'Feature',
  support: 'Support',
  general: 'General',
};

export const TAG_LABELS: Record<TicketTag, string> = {
  technical: 'Technical',
};

export const PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

/** Tailwind classes for each status. Kept beside the labels so a status can
 *  never be relabelled in one place and recoloured in another. */
export const STATUS_CLASSES: Record<TicketStatus, string> = {
  open: 'bg-rose-100 text-rose-800 border-rose-200',
  in_progress: 'bg-amber-100 text-amber-800 border-amber-200',
  waiting_on_customer: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  follow_up_needed: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200',
  resolved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  closed: 'bg-slate-100 text-slate-700 border-slate-200',
};

export const PRIORITY_CLASSES: Record<TicketPriority, string> = {
  low: 'bg-slate-100 text-slate-600 border-slate-200',
  normal: 'bg-sky-100 text-sky-800 border-sky-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  urgent: 'bg-red-100 text-red-800 border-red-200',
};

export const TYPE_CLASSES: Record<string, string> = {
  bug_report: 'bg-red-100 text-red-800 border-red-200',
  feature_request: 'bg-blue-100 text-blue-800 border-blue-200',
  support: 'bg-green-100 text-green-800 border-green-200',
  general: 'bg-gray-100 text-gray-800 border-gray-200',
};

export const SOURCE_CLASSES: Record<string, string> = {
  creator: 'bg-purple-100 text-purple-800 border-purple-200',
  brand: 'bg-amber-100 text-amber-800 border-amber-200',
  coach: 'bg-pink-100 text-pink-800 border-pink-200',
};

/** Which queue a ticket belongs to. A BMC Coach portal ticket is a creator
 *  ticket in the database (source = 'creator', is_coach = true, see migration
 *  20260930000002_support_coach_portal_tickets.sql) but support works it as
 *  its own queue, so everything that shows or filters by source goes
 *  through this instead of reading `source` directly. */
export type TicketQueue = 'creator' | 'brand' | 'coach';

export const QUEUE_LABELS: Record<TicketQueue, string> = {
  creator: 'Creator',
  brand: 'Brand',
  coach: 'BMC Coach',
};

export function ticketQueue(t: Pick<TicketRow, 'source' | 'is_coach'>): TicketQueue {
  if (t.is_coach) return 'coach';
  return t.source === 'brand' ? 'brand' : 'creator';
}

export const TAG_CLASSES: Record<string, string> = {
  technical: 'bg-cyan-100 text-cyan-800 border-cyan-200',
};

// ── BMC Assistant (AI first-line support) ─────────────────────────────────────
// See creator-app migration 20261002000002_support_ai_assistant.sql and the
// brand worker's supportAi.ts. The kill switch is the worker env var
// SUPPORT_AI_ENABLED — nothing here turns the AI on or off.

export type TicketHandledBy = 'human' | 'ai';
export type TicketAiStatus = 'active' | 'resolved' | 'handed_off';
export type AiHandoffReason =
  | 'user_requested' | 'no_answer' | 'account_specific' | 'sensitive' | 'attachment'
  | 'non_english' | 'max_replies' | 'agent_took_over' | 'reopened' | 'ai_error' | 'ai_disabled' | 'merged';

/** Mirrors MAX_AI_REPLIES_PER_TICKET in the worker's supportAi.ts (the DB keeps a looser backstop of 10). */
export const AI_MAX_REPLIES_PER_TICKET = 5;

export const AI_HANDOFF_REASON_LABELS: Record<AiHandoffReason, string> = {
  user_requested: 'Customer asked for a human',
  no_answer: 'Not covered by the FAQ',
  account_specific: 'Account-specific question',
  sensitive: 'Sensitive (billing, dispute, safety)',
  attachment: 'Customer sent an attachment',
  non_english: 'Not in English',
  max_replies: `Hit the ${AI_MAX_REPLIES_PER_TICKET}-reply limit`,
  agent_took_over: 'Agent took over',
  reopened: 'Reopened after AI resolved it',
  ai_error: 'AI error — handed off automatically',
  ai_disabled: 'AI switched off',
  merged: 'Merged with another ticket',
};

export const AI_STATUS_LABELS: Record<TicketAiStatus, string> = {
  active: 'AI handling',
  resolved: 'Resolved by AI',
  handed_off: 'Handed to human',
};

/** Queue presets on the Support Tickets page. Each is a pure filter over
 *  user_feedback_with_user (see applyAiQueue). */
export type AiQueue = 'all' | 'ai_handling' | 'ai_replied_24h' | 'needs_human' | 'ai_follow_up' | 'ai_resolved';

export const AI_QUEUE_LABELS: Record<AiQueue, string> = {
  all: 'All',
  ai_handling: 'AI handling',
  ai_replied_24h: 'AI replied (24h)',
  needs_human: 'Needs human',
  ai_follow_up: 'AI follow-up needed',
  ai_resolved: 'Resolved by AI',
};

/** Applies an AI queue preset to a user_feedback_with_user query. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function applyAiQueue(q: any, queue: AiQueue): any {
  switch (queue) {
    case 'ai_handling':
      return q.eq('handled_by', 'ai').eq('ai_status', 'active').in('status', ['open', 'in_progress', 'waiting_on_customer']);
    case 'ai_replied_24h':
      return q.gte('ai_last_replied_at', new Date(Date.now() - 24 * 3600 * 1000).toISOString());
    case 'needs_human':
      return q.eq('ai_awaiting_human', true).in('status', ['open', 'in_progress', 'waiting_on_customer', 'follow_up_needed']);
    case 'ai_follow_up':
      return q.eq('handled_by', 'ai').eq('status', 'follow_up_needed');
    case 'ai_resolved':
      return q.eq('ai_status', 'resolved');
    default:
      return q;
  }
}

/** One row of `user_feedback_with_user` — a ticket plus its requester identity
 *  and its thread state. */
export interface TicketRow {
  id: string;
  ticket_number: number | null;
  user_id: string | null;
  email: string | null;
  type: string;
  subject: string | null;
  message: string;
  status: TicketStatus;
  priority: TicketPriority;
  /** Triage labels — see TicketTag. Empty array, never null (DB default '{}'). */
  tags: string[];
  source: string | null;
  /** Opened from the BMC Coach portal. Always source = 'creator'. Use
   *  ticketQueue() rather than reading this and `source` separately. */
  is_coach: boolean;
  /** A merge combined an app ticket with a Coach portal ticket (same user), so
   *  this ticket shows in both the creator app and the Coach portal. */
  cross_portal: boolean;
  internal_note: string | null;
  assigned_admin_id: string | null;
  assigned_admin_email: string | null;
  assigned_at: string | null;
  first_response_at: string | null;
  resolved_at: string | null;
  closed_at: string | null;
  reopened_count: number;
  waiting_on_customer_since: string | null;
  follow_up_flagged_at: string | null;
  follow_up_flagged_by: 'system' | 'agent' | null;
  /** Support opened this ticket on the customer's behalf, rather than the
   *  customer opening it from Help & Support. */
  opened_by_admin: boolean;
  opened_by_admin_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  last_activity_at: string | null;
  display_name: string | null;
  roster_creator_id: string | null;
  conversation_id: string | null;
  conversation_status: 'active' | 'closed' | null;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_message_sender_type: SupportSenderType | null;
  last_customer_message_at: string | null;
  last_agent_message_at: string | null;
  message_count: number;
  /** No thread yet, or the customer sent the most recent visible message.
   *  The single best answer to "what should I work on next". */
  awaiting_reply: boolean;
  admin_unread_count: number;
  /** The brand's own contact_email/delivery_email (see migration
   *  20260921000004), when the ticket is attributed to one. NULL for a
   *  creator ticket, an unattributed legacy brand ticket, or a brand that
   *  never set a contact email — `email` (the submitter's own login email)
   *  is the fallback in all of those cases. */
  brand_contact_email: string | null;
  // ── BMC Assistant ──
  handled_by: TicketHandledBy;
  ai_status: TicketAiStatus | null;
  ai_reply_count: number;
  ai_last_replied_at: string | null;
  ai_handoff_reason: AiHandoffReason | null;
  ai_handed_off_at: string | null;
  ai_resolved_at: string | null;
  /** Handed off by the AI and no human has replied yet — ORed into awaiting_reply. */
  ai_awaiting_human: boolean;
  /** The AI's hand-off note for the agent (admin-only table). */
  ai_summary: string | null;
  /** BMC Coach portal ticket (source stays 'creator'). */
  is_coach: boolean;
  brand_id: string | null;
  // ── Merge (see migration 20261007000001_support_ticket_merge.sql) ──
  /** Set when this ticket was merged into another one. It is then closed,
   *  its messages live in that ticket's thread and customers no longer see it. */
  merged_into_ticket_id: string | null;
  merged_into_ticket_number: number | null;
  merged_at: string | null;
  merged_by: string | null;
  merged_by_name: string | null;
  /** How many tickets were merged INTO this one. */
  merged_ticket_count: number;
}

/** Shape mirrors the creator app's SupportAttachment / the brand<->creator
 *  messaging feature's MsgAttachment — same fields, own bucket
 *  ('support-attachments'), kept independent for the same reason the support
 *  tables are independent of cr_conversations/cr_messages. */
export interface SupportAttachment {
  url: string;
  path?: string;
  name: string;
  mime: string;
  size: number;
  kind: 'image' | 'file';
  width?: number;
  height?: number;
}

export interface SupportMessageRow {
  id: string;
  conversation_id: string;
  sender_type: SupportSenderType;
  sender_user_id: string | null;
  sender_name: string | null;
  body: string;
  is_internal: boolean;
  attachments: SupportAttachment[];
  created_at: string;
  edited_at: string | null;
  /** Written by BMC Assistant (always sender_type 'agent', no sender_user_id). */
  is_ai: boolean;
  /** The ticket this message was originally written on, when a merge moved it
   *  into this thread. NULL for messages that never moved. */
  merged_from_ticket_id: string | null;
}

/** A sender can fix a typo in their OWN message for this long after sending
 *  it — see migration 20260921000000_support_message_edit.sql for why it's
 *  time-boxed at all (unlike brand<->creator messaging's unlimited edit
 *  window): this table doubles as the record BMC support and the
 *  creator/brand both rely on. */
export const SUPPORT_MESSAGE_EDIT_WINDOW_MS = 5 * 60 * 1000;

/** Mirrors the RPC's own authorization + window check so the UI never offers
 *  an edit that would just fail server-side. sender_user_id is always the
 *  actual agent's id for an agent-sent message (never a shared "support"
 *  id), so this also keeps one admin from editing a teammate's reply. */
export function canEditMessage(m: Pick<SupportMessageRow, 'sender_type' | 'sender_user_id' | 'created_at'>, userId: string | null | undefined): boolean {
  if (!userId || m.sender_type === 'system' || m.sender_user_id !== userId) return false;
  return Date.now() - new Date(m.created_at).getTime() <= SUPPORT_MESSAGE_EDIT_WINDOW_MS;
}

export type SupportAttachmentKind = 'image' | 'video' | 'pdf' | 'file';

/** Classify by mime (falling back to stored kind / extension). Bucket-agnostic
 *  pure function — identical logic to the creator app's attachmentKind. */
export function attachmentKind(a: Pick<SupportAttachment, 'mime' | 'kind' | 'name'>): SupportAttachmentKind {
  const mime = (a.mime || '').toLowerCase();
  const name = (a.name || '').toLowerCase();
  if (mime.startsWith('image/') || a.kind === 'image') return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  return 'file';
}

/** Force a real download via Supabase's `download` query param. */
export function attachmentDownloadUrl(a: Pick<SupportAttachment, 'url' | 'name'>): string {
  if (!a.url) return a.url;
  const sep = a.url.includes('?') ? '&' : '?';
  return `${a.url}${sep}download=${encodeURIComponent(a.name || 'file')}`;
}

export function formatAttachmentSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface TicketEventRow {
  id: string;
  ticket_id: string;
  actor_kind: 'customer' | 'agent' | 'system';
  actor_name: string | null;
  event_type: string;
  from_value: string | null;
  to_value: string | null;
  note: string | null;
  created_at: string;
}

function firstMessageLine(message: string | null | undefined): string | null {
  const line = (message || '').split('\n').find((l) => l.trim().length > 0)?.trim();
  if (!line) return null;
  return line.length > 80 ? `${line.slice(0, 80)}…` : line;
}

/** Titles come from `subject` when present, else the first line of the original
 *  message — every ticket predating subjects still needs a title.
 *
 *  BMC Coach tickets are the exception: the portal seeds the subject from a
 *  suggested topic ("Connecting Claude"), so many tickets share one. They're
 *  titled by the creator's first message instead, with the subject shown
 *  separately via ticketSubtitle(). */
export function ticketTitle(t: Pick<TicketRow, 'subject' | 'message' | 'type'> & { is_coach?: boolean }): string {
  const subject = t.subject?.trim();
  const firstLine = firstMessageLine(t.message);
  if (t.is_coach && firstLine) return firstLine;
  if (subject) return subject;
  if (firstLine) return firstLine;
  return TYPE_LABELS[t.type as TicketType] ?? 'Support request';
}

/** The subject, when ticketTitle() isn't already showing it (coach tickets
 *  only). Null otherwise, so callers render nothing. */
export function ticketSubtitle(t: Pick<TicketRow, 'subject' | 'message' | 'type'> & { is_coach?: boolean }): string | null {
  const subject = t.subject?.trim();
  if (!subject || !t.is_coach || !firstMessageLine(t.message)) return null;
  return subject;
}

/** Minutes from ticket creation to the first agent reply, or null while the
 *  SLA clock is still running. */
export function firstResponseMinutes(t: Pick<TicketRow, 'created_at' | 'first_response_at'>): number | null {
  if (!t.created_at || !t.first_response_at) return null;
  const ms = new Date(t.first_response_at).getTime() - new Date(t.created_at).getTime();
  return ms >= 0 ? Math.round(ms / 60000) : null;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

// ── Reads ───────────────────────────────────────────────────────────────────

export async function getTicket(ticketId: string): Promise<TicketRow | null> {
  const { data, error } = await supabase
    .from('user_feedback_with_user')
    .select('*')
    .eq('id', ticketId)
    .maybeSingle();
  if (error) throw error;
  return (data as TicketRow) ?? null;
}

/** Full thread INCLUDING internal notes — the admin's token can read them. */
const ATTACH_BUCKET = 'support-attachments';
const ATTACH_SIGNED_TTL = 60 * 60 * 24 * 7; // 7 days
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
export const MAX_ATTACHMENTS_PER_MESSAGE = 10;

/** The bucket is private and the URL stored on the message is signed with a
 *  7-day TTL, so stored URLs go dead. Derive the storage path (stored for real
 *  uploads; parsed out of the signed URL for older rows) so it can be
 *  re-signed on fetch. */
function attachmentStoragePath(a: SupportAttachment): string | null {
  if (a.path) return a.path;
  const m = a.url?.match(/\/object\/sign\/support-attachments\/([^?]+)/);
  return m ? m[1] : null;
}

async function resignAttachmentUrls(messages: SupportMessageRow[]): Promise<SupportMessageRow[]> {
  const paths = new Set<string>();
  for (const m of messages) {
    for (const a of m.attachments ?? []) {
      const p = attachmentStoragePath(a);
      if (p) paths.add(p);
    }
  }
  if (paths.size === 0) return messages;
  const { data, error } = await supabase.storage
    .from(ATTACH_BUCKET)
    .createSignedUrls([...paths], ATTACH_SIGNED_TTL);
  if (error || !data) return messages; // fall back to the stored URLs
  const urlByPath = new Map<string, string>();
  for (const d of data) {
    if (d.path && d.signedUrl) urlByPath.set(d.path, d.signedUrl);
  }
  if (urlByPath.size === 0) return messages;
  return messages.map((m) => {
    if (!m.attachments?.length) return m;
    return {
      ...m,
      attachments: m.attachments.map((a) => {
        const p = attachmentStoragePath(a);
        const url = p ? urlByPath.get(p) : undefined;
        return url ? { ...a, url } : a;
      }),
    };
  });
}

/** Uploads one browser File into the conversation's storage folder and
 *  returns the attachment record ready to send. Path convention matches the
 *  creator app's uploadSupportAttachment so both sides' RLS folder checks
 *  (storage.foldername(name)[1] against support_conversations) agree. */
export async function uploadAttachment(conversationId: string, file: File): Promise<SupportAttachment> {
  const kind: SupportAttachment['kind'] = file.type.startsWith('image/') ? 'image' : 'file';
  const path = `${conversationId}/${Date.now()}-${file.name.replace(/[^\w.\-]/g, '_')}`;
  const { error } = await supabase.storage.from(ATTACH_BUCKET).upload(path, file, { contentType: file.type });
  if (error) throw error;
  const { data } = await supabase.storage.from(ATTACH_BUCKET).createSignedUrl(path, ATTACH_SIGNED_TTL);
  // Images: read natural dimensions client-side so bubbles can size themselves
  // without a layout jump once the file loads.
  let width: number | undefined;
  let height: number | undefined;
  if (kind === 'image') {
    try {
      const dims = await new Promise<{ w: number; h: number }>((resolve, reject) => {
        const img = new window.Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
      width = dims.w;
      height = dims.h;
    } catch { /* dimensions are a nicety, not required */ }
  }
  return {
    url: data?.signedUrl ?? '', path, name: file.name, mime: file.type || 'application/octet-stream',
    size: file.size, kind, width, height,
  };
}

export async function getMessages(conversationId: string): Promise<SupportMessageRow[]> {
  const { data, error } = await supabase
    .from('support_messages')
    .select('id, conversation_id, sender_type, sender_user_id, sender_name, body, is_internal, attachments, created_at, edited_at, is_ai, merged_from_ticket_id')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return resignAttachmentUrls((data ?? []) as SupportMessageRow[]);
}

export async function getEvents(ticketId: string): Promise<TicketEventRow[]> {
  const { data, error } = await supabase
    .from('support_ticket_events')
    .select('id, ticket_id, actor_kind, actor_name, event_type, from_value, to_value, note, created_at')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as TicketEventRow[];
}

export interface SupportAgent {
  user_id: string;
  email: string;
  display_name: string | null;
}

/**
 * Active admins, for the assignee picker. Reads the existing `admin_users`
 * roster rather than inventing a support-specific one. Returns [] rather than
 * throwing if that table is unreadable — the detail page falls back to
 * "Assign to me", which covers the common case.
 */
export async function listAgents(): Promise<SupportAgent[]> {
  const { data, error } = await supabase
    .from('admin_users')
    .select('user_id, email, display_name')
    .eq('is_active', true)
    .order('email');
  if (error) return [];
  return (data ?? []) as SupportAgent[];
}

/** The creator's profile full name. The ticket view's display_name prefers the
 *  TikTok handle, so the real name isn't on the ticket row. Null when unset or
 *  unreadable — the Requester card just omits the line. */
export async function getCreatorFullName(userId: string): Promise<string | null> {
  const { data, error } = await supabase.from('profiles').select('full_name').eq('id', userId).maybeSingle();
  if (error) return null;
  return data?.full_name?.trim() || null;
}

/** Full names for a page of creator tickets in ONE query, keyed by profile id.
 *  Empty on failure — callers just render without the name. */
export async function getCreatorFullNames(userIds: string[]): Promise<Record<string, string>> {
  if (userIds.length === 0) return {};
  const { data, error } = await supabase.from('profiles').select('id, full_name').in('id', userIds);
  if (error) return {};
  const out: Record<string, string> = {};
  for (const r of data ?? []) if (r.full_name?.trim()) out[r.id] = r.full_name.trim();
  return out;
}

/** A creator the support team can open a ticket for. */
export interface SupportCustomer {
  id: string;
  email: string | null;
  full_name: string | null;
  tiktok_handle: string | null;
}

/**
 * Creator search for the "New Ticket" picker. Capped at 20 and filtered
 * server-side — the roster is far too large to pull down and filter in the
 * browser. Brands are excluded for now: support_admin_open_ticket() is still
 * hardcoded to source: 'creator' server-side (migration
 * 20260917000000_support_admin_created_tickets.sql) — the queue's chat screen
 * itself already works for a brand ticket, so widening this to open one on a
 * brand's behalf is a separate follow-up, not a chat-screen limitation.
 */
export async function searchCustomers(term: string): Promise<SupportCustomer[]> {
  let q = supabase
    .from('profiles')
    .select('id, email, full_name, tiktok_handle')
    .eq('role', 'creator')
    .limit(20);

  const search = term.trim();
  if (search) {
    const escaped = search.replace(/[%,()]/g, '');
    q = q.or(`email.ilike.%${escaped}%,full_name.ilike.%${escaped}%,tiktok_handle.ilike.%${escaped}%`);
  } else {
    q = q.order('created_at', { ascending: false });
  }

  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as SupportCustomer[];
}

export function customerLabel(c: SupportCustomer): string {
  return c.tiktok_handle?.trim() || c.full_name?.trim() || c.email || 'Unknown creator';
}

/** The signed-in admin, for "Assign to me" and for marking own messages. */
export async function currentAgent(): Promise<{ id: string; email: string | null } | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user ? { id: user.id, email: user.email ?? null } : null;
}

/** Why `candidate` can't be merged into `primary`, or null when it can.
 *  Mirrors the checks inside support_merge_tickets(), which is the real gate. */
export function mergeBlockReason(
  primary: Pick<TicketRow, 'id' | 'user_id' | 'source' | 'brand_id' | 'is_coach'>,
  candidate: TicketRow,
): string | null {
  if (candidate.merged_into_ticket_id) {
    return candidate.merged_into_ticket_id === primary.id
      ? 'Already merged into this ticket'
      : `Already merged into #${candidate.merged_into_ticket_number ?? '—'}`;
  }
  if ((candidate.source ?? null) !== (primary.source ?? null)) {
    return `Opened from the ${candidate.source ?? 'legacy'} portal, not ${primary.source ?? 'legacy'}`;
  }
  const sameUser = !!candidate.user_id && candidate.user_id === primary.user_id;
  const sameBrand = primary.source === 'brand' && !!primary.brand_id && candidate.brand_id === primary.brand_id;
  if (!sameUser && !sameBrand) return 'Opened from a different account';
  return null;
}

/** Everything that could belong to the same customer as `primary`: same
 *  requester, same brand, or the same email address. Tickets that fail the
 *  merge rules are still returned so the dialog can show them as unavailable
 *  and say why, instead of silently hiding them (see mergeBlockReason). */
export async function listMergeCandidates(primary: Pick<TicketRow, 'id' | 'user_id' | 'source' | 'brand_id' | 'is_coach' | 'email'>): Promise<TicketRow[]> {
  const ors: string[] = [];
  if (primary.user_id) ors.push(`user_id.eq.${primary.user_id}`);
  if (primary.source === 'brand' && primary.brand_id) ors.push(`brand_id.eq.${primary.brand_id}`);
  if (primary.email) ors.push(`email.ilike.${primary.email.replace(/[,()%*]/g, '')}`);
  if (ors.length === 0) return [];
  const { data, error } = await supabase
    .from('user_feedback_with_user')
    .select('*')
    .neq('id', primary.id)
    .or(ors.join(','))
    .order('last_activity_at', { ascending: false, nullsFirst: false })
    .limit(100);
  if (error) throw error;
  return (data ?? []) as TicketRow[];
}

/** Tickets that were merged into this one, oldest first. */
export async function listMergedTickets(primaryId: string): Promise<Pick<TicketRow, 'id' | 'ticket_number' | 'subject' | 'message' | 'type' | 'created_at' | 'merged_at' | 'merged_by_name'>[]> {
  const { data, error } = await supabase
    .from('user_feedback')
    .select('id, ticket_number, subject, message, type, created_at, merged_at, merged_by_name')
    .eq('merged_into_ticket_id', primaryId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

// ── Writes ──────────────────────────────────────────────────────────────────

export interface MergeResult {
  primary_ticket_id: string;
  primary_ticket_number: number | null;
  merged_ticket_ids: string[];
  merged_count: number;
  moved_message_count: number;
}

/**
 * Merges duplicate tickets into `primary`. Their messages move into the
 * primary's thread, they are closed and hidden from the customer, and the
 * customer sees one line in the primary saying what was merged. All of it
 * happens in support_merge_tickets() in one transaction, which also records
 * who merged what (merged_* columns + 'merged'/'merged_in' events).
 */
export async function mergeTickets(
  primary: Pick<TicketRow, 'id' | 'ticket_number'>,
  duplicates: Pick<TicketRow, 'id' | 'ticket_number' | 'status'>[],
  note?: string | null,
): Promise<MergeResult> {
  const dupIds = duplicates.map((d) => d.id);
  const dupNumbers = duplicates.map((d) => d.ticket_number);
  const { data, error } = await supabase.rpc('support_merge_tickets', {
    p_primary_ticket_id: primary.id,
    p_ticket_ids: dupIds,
    p_note: note?.trim() || null,
  });
  if (error) {
    await logUpdate('user_feedback', primary.id, { merged_ticket_ids: [] }, { merged_ticket_ids: dupIds }, {
      description: `Failed to merge ${duplicates.length} ticket(s) into #${primary.ticket_number ?? '—'}`,
      status: 'failed',
      error: error.message,
      merged_ticket_numbers: dupNumbers,
    });
    throw error;
  }
  const result = data as MergeResult;
  await logUpdate('user_feedback', primary.id, { merged_ticket_ids: [] }, { merged_ticket_ids: dupIds }, {
    description: `Merged ${duplicates.length} ticket(s) into #${primary.ticket_number ?? '—'}`,
    merged_ticket_numbers: dupNumbers,
    previous_statuses: Object.fromEntries(duplicates.map((d) => [d.id, d.status])),
    moved_message_count: result?.moved_message_count ?? null,
    ...(note?.trim() ? { note: note.trim() } : {}),
  });
  return result;
}

export interface AdminOpenedTicket {
  ticketId: string;
  conversationId: string;
  ticketNumber: number | null;
}

/**
 * Opens a ticket on a customer's behalf. The ticket lands at 'open' with an
 * EMPTY thread — nothing has been said yet — and the agent's first message in
 * that thread is what moves it to 'in_progress' (see migration
 * 20260919000000_support_admin_created_tickets.sql). `message` is the ticket
 * body and is customer-visible: the creator app shows it as the request's
 * preview until the first message lands.
 */
export async function adminOpenTicket(params: {
  userId: string;
  type: TicketType;
  subject: string;
  message: string;
  priority: TicketPriority;
}): Promise<AdminOpenedTicket> {
  const payload = {
    p_user_id: params.userId,
    p_type: params.type,
    p_subject: params.subject.trim(),
    p_message: params.message.trim(),
    p_priority: params.priority,
  };
  const { data, error } = await supabase.rpc('support_admin_open_ticket', payload);
  if (error) {
    await logCreate('user_feedback', params.userId, payload, {
      description: 'Failed to open support ticket for a customer',
      status: 'failed',
      error: error.message,
    });
    throw error;
  }

  // RETURNS TABLE arrives as a single-row array.
  const row = Array.isArray(data) ? data[0] : data;
  if (!row?.ticket_id || !row?.conversation_id) {
    throw new Error('The ticket was created but no chat came back. Reopen it from the queue.');
  }

  await logCreate('user_feedback', row.ticket_id, {
    user_id: params.userId,
    type: params.type,
    subject: params.subject.trim(),
    priority: params.priority,
    ticket_number: row.ticket_number ?? null,
    opened_by_admin: true,
  }, {
    description: `Opened support ticket #${row.ticket_number ?? '—'} for a customer`,
  });

  return {
    ticketId: row.ticket_id,
    conversationId: row.conversation_id,
    ticketNumber: row.ticket_number ?? null,
  };
}

/**
 * Opens the thread for a ticket, building it from the ticket's original message
 * if it doesn't exist yet. Idempotent — safe to call on every detail-page load,
 * which is exactly how a pre-feature ticket (creator or brand) gets a chat.
 */
export async function ensureConversation(ticketId: string): Promise<string> {
  const { data, error } = await supabase.rpc('support_get_or_create_conversation', {
    p_ticket_id: ticketId,
  });
  if (error) {
    await logCreate('support_conversations', ticketId, { ticket_id: ticketId }, {
      description: 'Failed to open support conversation',
      status: 'failed',
      error: error.message,
    });
    throw error;
  }
  return data as string;
}

export async function postMessage(
  conversationId: string,
  ticketId: string,
  body: string,
  isInternal: boolean,
  attachments: SupportAttachment[] = [],
): Promise<string> {
  const { data, error } = await supabase.rpc('support_post_message', {
    p_conversation_id: conversationId,
    p_body: body.trim(),
    p_is_internal: isInternal,
    p_attachments: attachments,
  });
  if (error) {
    await logCreate('support_messages', ticketId, {
      conversation_id: conversationId, is_internal: isInternal, attachment_count: attachments.length,
    }, {
      description: isInternal ? 'Failed to add internal note' : 'Failed to send support reply',
      status: 'failed',
      error: error.message,
    });
    throw error;
  }
  await logCreate('support_messages', String(data), {
    conversation_id: conversationId,
    ticket_id: ticketId,
    is_internal: isInternal,
    body_length: body.trim().length,
    attachment_count: attachments.length,
  }, {
    description: isInternal ? 'Added internal note to ticket' : 'Replied to support ticket',
  });
  return data as string;
}

/** Edits an agent's own message within the 5-minute window — see
 *  canEditMessage()/migration 20260921000000_support_message_edit.sql.
 *  support_edit_message() re-checks ownership and the window server-side. */
export async function editMessage(messageId: string, oldBody: string, newBody: string): Promise<void> {
  const { error } = await supabase.rpc('support_edit_message', {
    p_message_id: messageId,
    p_body: newBody.trim(),
  });
  if (error) {
    await logUpdate('support_messages', messageId, { body: oldBody }, { body: newBody.trim() }, {
      description: 'Failed to edit support message',
      status: 'failed',
      error: error.message,
    });
    throw error;
  }
  await logUpdate('support_messages', messageId, { body: oldBody }, { body: newBody.trim() }, {
    description: 'Edited support message',
  });
}

/** Stops BMC Assistant on a ticket before it replies again. No customer-visible
 *  message — the customer simply gets a human reply next. */
export async function takeOverFromAi(ticketId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('support_admin_ai_take_over', { p_ticket_id: ticketId });
  if (error) {
    await logUpdate('user_feedback', ticketId, { handled_by: 'ai' }, { handled_by: 'human' }, {
      description: 'Failed to take over a ticket from BMC Assistant',
      status: 'failed',
      error: error.message,
    });
    throw error;
  }
  await logUpdate('user_feedback', ticketId, { handled_by: 'ai' }, { handled_by: 'human' }, {
    description: 'Took over a ticket from BMC Assistant',
    changes: { handled_by: 'human', ai_handoff_reason: 'agent_took_over', applied: data === true },
  });
  return data === true;
}

export interface AiUnansweredRow {
  id: string;
  ticket_id: string | null;
  portal: 'creator' | 'brand' | 'coach';
  question: string | null;
  reason: AiHandoffReason;
  /** For reason = ai_error: the internal error behind the hand-off. */
  error_detail: string | null;
  created_at: string;
  /** Filled client-side from the ticket so a row can be matched to it. */
  ticket_number?: number | null;
  ticket_subject?: string | null;
  /** The ticket's opening message — what the customer first wrote. */
  first_message?: string | null;
}

/** Every AI -> human handoff, newest first, any reason — not just the
 *  no_answer ones with a rephrased FAQ-gap question (question is null for
 *  every other reason). The to-do list for both "what's the AI missing from
 *  the FAQ" and "what's still landing on a human". */
export async function listAiUnanswered(page: number, pageSize: number): Promise<{ rows: AiUnansweredRow[]; total: number }> {
  const from = page * pageSize;
  const { data, error, count } = await supabase
    .from('support_ai_unanswered')
    .select('id, ticket_id, portal, question, reason, error_detail, created_at', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(from, from + pageSize - 1);
  if (error) throw error;
  const rows = (data ?? []) as AiUnansweredRow[];
  await attachTicketInfo(rows);
  return { rows, total: count ?? 0 };
}

/** Adds each row's ticket number, subject and opening message — the same identifiers the
 *  Feedback list shows. Fail-soft: the list still works without them. */
async function attachTicketInfo(rows: AiUnansweredRow[]): Promise<void> {
  try {
    const ticketIds = [...new Set(rows.map(r => r.ticket_id).filter(Boolean))] as string[];
    if (!ticketIds.length) return;
    const { data, error } = await supabase
      .from('user_feedback')
      .select('id, ticket_number, subject, message')
      .in('id', ticketIds);
    if (error) throw error;
    const byTicket = new Map((data ?? []).map((t: any) => [t.id, t]));
    for (const r of rows) {
      const t: any = r.ticket_id ? byTicket.get(r.ticket_id) : null;
      if (!t) continue;
      r.ticket_number = t.ticket_number ?? null;
      r.ticket_subject = t.subject ?? null;
      r.first_message = t.message ?? null;
    }
  } catch (e) {
    console.warn('[ai-unanswered] could not load ticket info', e);
  }
}

export async function setTicketStatus(
  ticketId: string,
  from: TicketStatus,
  to: TicketStatus,
  note?: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('support_set_ticket_status', {
    p_ticket_id: ticketId,
    p_status: to,
    p_note: note?.trim() || null,
  });
  if (error) {
    await logUpdate('user_feedback', ticketId, { status: from }, { status: to }, {
      description: `Failed to set ticket status to ${to}`,
      status: 'failed',
      error: error.message,
    });
    throw error;
  }
  await logUpdate('user_feedback', ticketId, { status: from }, { status: to }, {
    description: `Set ticket status to ${STATUS_LABELS[to]}`,
    changes: { status: to, ...(note ? { note } : {}) },
  });
}

export async function setTicketPriority(
  ticketId: string,
  from: TicketPriority,
  to: TicketPriority,
): Promise<void> {
  const { error } = await supabase.rpc('support_set_ticket_priority', {
    p_ticket_id: ticketId,
    p_priority: to,
  });
  if (error) {
    await logUpdate('user_feedback', ticketId, { priority: from }, { priority: to }, {
      description: `Failed to set ticket priority to ${to}`,
      status: 'failed',
      error: error.message,
    });
    throw error;
  }
  await logUpdate('user_feedback', ticketId, { priority: from }, { priority: to }, {
    description: `Set ticket priority to ${PRIORITY_LABELS[to]}`,
    changes: { priority: to },
  });
}

/** Assigns to an admin user id, or unassigns when adminId is null. */
export async function assignTicket(
  ticketId: string,
  fromEmail: string | null,
  adminId: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('support_assign_ticket', {
    p_ticket_id: ticketId,
    p_admin_id: adminId,
  });
  if (error) {
    await logUpdate('user_feedback', ticketId, { assigned_admin_email: fromEmail }, { assigned_admin_id: adminId }, {
      description: adminId ? 'Failed to assign ticket' : 'Failed to unassign ticket',
      status: 'failed',
      error: error.message,
    });
    throw error;
  }
  await logUpdate('user_feedback', ticketId, { assigned_admin_email: fromEmail }, { assigned_admin_id: adminId }, {
    description: adminId ? 'Assigned support ticket' : 'Unassigned support ticket',
    changes: { assigned_admin_id: adminId },
  });
}

/** Internal notes stored on the ticket itself (distinct from an internal note
 *  posted into the thread). Kept because the queue page has always had it. */
export async function saveInternalNote(ticketId: string, from: string | null, note: string): Promise<void> {
  const { error } = await supabase
    .from('user_feedback')
    .update({ internal_note: note })
    .eq('id', ticketId);
  if (error) {
    await logUpdate('user_feedback', ticketId, { internal_note: from }, { internal_note: note }, {
      description: 'Failed to save ticket internal note',
      status: 'failed',
      error: error.message,
    });
    throw error;
  }
  await logUpdate('user_feedback', ticketId, { internal_note: from }, { internal_note: note }, {
    description: 'Saved ticket internal note',
  });
}

/** Sets a ticket's tag from the Manage dropdown — a ticket carries at most ONE
 *  triage tag at a time (never several). Passing `null` clears it back to
 *  untagged. Replaces the whole `tags` array (rather than add/remove one
 *  entry) so picking a new tag from the dropdown always replaces, never
 *  stacks on top of, whatever was there before. The queue table (Feedback.tsx)
 *  only ever reads this value now — it renders a plain badge, not a control. */
export async function setTicketTag(ticketId: string, currentTags: string[], tag: TicketTag | null): Promise<void> {
  const nextTags = tag ? [tag] : [];
  const { error } = await supabase
    .from('user_feedback')
    .update({ tags: nextTags })
    .eq('id', ticketId);
  if (error) {
    await logUpdate('user_feedback', ticketId, { tags: currentTags }, { tags: nextTags }, {
      description: `Failed to set ticket tag to ${tag ? TAG_LABELS[tag] : 'None'}`,
      status: 'failed',
      error: error.message,
    });
    throw error;
  }
  await logUpdate('user_feedback', ticketId, { tags: currentTags }, { tags: nextTags }, {
    description: `Set ticket tag to ${tag ? TAG_LABELS[tag] : 'None'}`,
  });
}

/** This agent's read cursor for one thread, as it is right now. Call it BEFORE
 *  markConversationRead(): opening a ticket advances the cursor, and the "New
 *  messages" line needs the value from before that. */
export async function fetchReadCursor(conversationId: string): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.id) return null;
  const { data, error } = await supabase
    .from('support_conversation_reads')
    .select('last_read_at')
    .eq('conversation_id', conversationId)
    .eq('user_id', user.id)
    .maybeSingle();
  if (error) throw error;
  return (data as { last_read_at: string | null } | null)?.last_read_at ?? null;
}

/** The message the "New messages" line goes above: the first CUSTOMER message
 *  newer than the cursor. Same rule as the queue's admin_unread_count, which
 *  counts customer messages only, so a teammate's reply or a status update
 *  never raises it. Null when nothing is unread, or when the unread run
 *  starts at the very top of the thread, where a line would separate nothing. */
export function firstUnreadCustomerMessageId(messages: SupportMessageRow[], lastReadAt: string | null): string | null {
  const cursor = lastReadAt ? new Date(lastReadAt).getTime() : null;
  const idx = messages.findIndex((m) => m.sender_type === 'customer'
    && (cursor === null || new Date(m.created_at).getTime() > cursor));
  return idx > 0 ? messages[idx].id : null;
}

/** Advances this agent's read cursor so the queue's unread count clears. */
export async function markConversationRead(conversationId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.id) return;
  await supabase
    .from('support_conversation_reads')
    .upsert(
      { conversation_id: conversationId, user_id: user.id, last_read_at: new Date().toISOString() },
      { onConflict: 'conversation_id,user_id' },
    );
}

/** "Mark as unread" for this agent. Moves the read cursor back to just after
 *  the last human agent message (a reply or an internal note — AI replies and
 *  system lines don't count) that came before the newest customer message.
 *  So only the latest run of customer messages — the ones nobody has answered
 *  since — counts as unread again, exactly as it did before the ticket was
 *  opened; earlier, already-answered messages stay read. No agent message yet
 *  means the whole customer side is unread. The cursor is per agent, so
 *  teammates are unaffected. Returns false when there is no customer message
 *  to mark. The column is NOT NULL, hence the epoch rather than null. */
export async function markConversationUnread(conversationId: string): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.id) throw new Error('You must be signed in');

  const { data: lastCustomer, error: custError } = await supabase
    .from('support_messages')
    .select('created_at')
    .eq('conversation_id', conversationId)
    .eq('sender_type', 'customer')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (custError) throw custError;
  if (!lastCustomer) return false;

  const { data: lastAgent, error: agentError } = await supabase
    .from('support_messages')
    .select('created_at')
    .eq('conversation_id', conversationId)
    .eq('sender_type', 'agent')
    .eq('is_ai', false)
    .lt('created_at', lastCustomer.created_at)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (agentError) throw agentError;

  const { error } = await supabase
    .from('support_conversation_reads')
    .upsert(
      { conversation_id: conversationId, user_id: user.id, last_read_at: lastAgent?.created_at ?? new Date(0).toISOString() },
      { onConflict: 'conversation_id,user_id' },
    );
  if (error) throw error;
  return true;
}

/** Live thread updates for the open ticket detail page. UPDATE is included
 *  (not just INSERT) so an edit — the agent's own, or the customer's — shows
 *  up live instead of only after the next reload. */
export function subscribeToThread(conversationId: string, onChange: () => void) {
  const channel = supabase
    .channel(`admin_support_thread:${conversationId}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'support_messages', filter: `conversation_id=eq.${conversationId}` },
      () => onChange(),
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'support_messages', filter: `conversation_id=eq.${conversationId}` },
      () => onChange(),
    )
    .subscribe();
  return () => { supabase.removeChannel(channel); };
}
